/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isNil from 'lodash/isNil';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';

import Stringify from '../../stringify';

/**
 * Popis významu viz README.md, udržovat v syncu.
 * @typedef {never
 *   | 'autoreboot.disabled'
 *   | 'autoreboot.interval'
 *   | 'control.local.exceptions.power'
 *   | 'control.local.locked'
 *   | 'control.remote.exceptions.power'
 *   | 'control.remote.locked'
 *   | 'firmware.disabled'
 *   | 'generic.viewer.video.preloadBlackeningFix'
 *   | 'generic.viewer.video.preloadBlackeningFixDelay'
 *   | 'generic.viewer.video.seekDisable'
 *   | 'player.light.idlePopup.timeoutInSec'
 *   | 'player.light.screen.0.cssOrientation'
 *   | 'player.light.viewer.transition'
 *   | 'player.light.viewer.video.preloadDisable'
 *   | 'storage.types'
 * } KnownKeys
 */

/**
 * @import { LiteralUnion } from '../../../util.type'
 */

/**
 * @typedef {LiteralUnion<KnownKeys, string>} CommunicatorSettingsKey
 * @typedef {Readonly<Partial<Record<CommunicatorSettingsKey, string>>>} CommunicatorSettingsJson
 */

/**
 * @typedef {Object} CommunicatorSettingsGetter
 * @property {((key: CommunicatorSettingsKey) => string | undefined)} get Zjistí hodnotu položky nastavení.
 * Vrací hodnotu nalezené položky nebo `undefined`.
 * @property {() => Record<string, string>} getAll Vrátí všechny položky nastavení.
 */

// TODO: Podivné, že se připouští volitelnost `this.json` a že jsou nutné runtime kontroly.
// Není lepší defaultovat za `{}` v konstruktoru a zbavit se runtime kontrol v `get`?
/**
 * Komunikátor univerzálního nastavení pro Ki-Wi Server.
 * Knihovna utilit pro operace s nastavením zařízení v `key:value` formátu.
 * @implements {CommunicatorSettingsGetter}
 */
export default class CommunicatorSettings {
  /**
   * Vytvoří instanci. V konstruktoru dojde k modifikaci předaného json objektu
   * zavoláním Object.freeze. Pokud toto chování je nechtěné,
   * tak se musí předat kopie původního objektu.
   * @param {CommunicatorSettingsJson} [json] Část obsahu zařízení ze serveru s nastavením.
   */
  constructor(json) {
    if (!isNil(json) && !isPlainObject(json)) {
      throw new TypeError(`Invalid json "${json}".`);
    }

    // TODO: Otázka, zda stačí informace true/false nebo chceme i podrobnosti?
    const nonStringValueEntries = Object.entries(json ?? {}).reduce(
      (acc, entry) => {
        if (!isString(entry[1])) {
          acc.push(entry);
        }
        return acc;
      },
      /** @type {[string, any][]} */ ([])
    );
    if (nonStringValueEntries.length > 0) {
      throw new TypeError(
        `Some values are not string: ${Stringify.valueSafeInformal(
          Object.fromEntries(nonStringValueEntries)
        )}`
      );
    }

    this.json = /** @type {Record<string, string>} */ (json ?? {});
    Object.freeze(this.json);
  }

  // TODO #555: Jak zabránit modifikacím a přiřazení? Nebo jen exportovat konstantu?
  // Nebo udělat raději nějakou NOP implementaci k rozhraní?
  /**
   * Prázdné nastavení.
   * @readonly
   */
  static get empty() {
    return emptySettings;
  }

  /**
   * Zjistí hodnotu položky nastavení.
   * @type {CommunicatorSettingsGetter['get']}
   */
  get(key) {
    const { json } = this;
    if (!Object.prototype.hasOwnProperty.call(json, key)) {
      return undefined;
    }
    return json[key];
  }

  /**
   * @type {CommunicatorSettingsGetter['getAll']}
   */
  getAll() {
    return this.json;
  }
}

/** @readonly */
const emptySettings = new CommunicatorSettings();
